import Styles from '../../components/about/About.module.css';
import { AboutFooter } from '../../components/about/AboutFooter';
import { AboutHeader } from '../../components/about/AboutHeader';
import { CompanyTab } from '../../components/about/CompanyTab';
import { DemoTab } from '../../components/about/DemoTab';
import { HelpTab } from '../../components/about/HelpTab';
import { TermsTab } from '../../components/about/TermsTab';
import { useLocale } from '../../providers/LocaleProvider';
import { Telemetry } from '@shared/frontend';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Extracts the active tab key from the current pathname. Routes are exact: /about/demo, /about/company, /about/terms, /about/help.
 *
 * @param pathname - The current URL pathname.
 * @returns The tab key (e.g. 'demo', 'company', 'terms', 'help').
 */
function GetTabFromPathname(pathname: string): string {
  const lastSegment = pathname.split('/').pop();
  return lastSegment ?? 'demo';
}

/**
 * Maps a route tab parameter to its rendered tab component.
 *
 * @param tab - The tab key from the URL.
 * @returns The tab component element.
 */
function RenderTab(tab: string) {
  switch (tab) {
    case 'demo':
      return <DemoTab />;
    case 'company':
      return <CompanyTab />;
    case 'terms':
      return <TermsTab />;
    case 'help':
      return <HelpTab />;
    default:
      return <DemoTab />;
  }
}

/**
 * Public About page. A single scrolling page with a sticky header, tab-based content, and a footer. Each tab is reachable via its own sub-route for direct linking.
 *
 * Direct links:
 *
 * - /about/demo → Demo tab (also the default when visiting /about)
 * - /about/company → Company tab.
 * - /about/terms → Terms tab.
 * - /about/help → Help tab.
 *
 * @returns The about page element.
 */
export function About() {
  const location = useLocation();
  const { langVersion } = useLocale();
  const activeTab = GetTabFromPathname(location.pathname);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Telemetry.Navigation(location.pathname);
    Telemetry.FeatureUsed(`about_tab_${activeTab}`);
  }, [activeTab, location.pathname]);

  useEffect(() => {
    if (rootRef.current) rootRef.current.scrollTop = 0;
  }, [activeTab]);

  return (
    <div ref={rootRef} className={Styles.aboutRoot} data-lang-version={langVersion}>
      <div className={Styles.heroGrid} aria-hidden="true" />
      <div className={Styles.heroGlow} aria-hidden="true" />
      <div className={Styles.heroGlow2} aria-hidden="true" />
      <AboutHeader />
      <main className={Styles.content}>{RenderTab(activeTab)}</main>
      <AboutFooter />
    </div>
  );
}
